import React, { useEffect, useMemo, useState } from 'react';

import { useTheme } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { CallEnd, Close, ChatBubbleOutline } from '@mui/icons-material';

import { DailyAudio, useAppMessage, useDaily } from '@daily-co/daily-react';

import { useSessionContext } from '../../contexts/SessionContext';

import useSession from '../../hooks/useSession';
import { useInspectorStyles } from './styles';

import ZoomIcon from '../../icons/ZoomIcon';
import ZoomInIcon from '../../icons/ZoomInIcon';
import ZoomOutIcon from '../../icons/ZoomOutIcon';
import ResolutionIcon from '../../icons/ResolutionIcon';
import ResolutionDownIcon from '../../icons/ResolutionDownIcon';
import ResolutionUpIcon from '../../icons/ResolutionUpIcon';
import ChangeCameraIcon from '../../icons/ChangeCameraIcon';
import CaptureIcon from '../../icons/CaptureIcon';
import SpeakerIcon from '../../icons/SpeakerIcon';
import MicrophoneIcon from '../../icons/MicrophoneIcon';

import CustomButton from '../../components/CustomButton';
import ModalContentWrapper from '../../components/ModalContentWrapper';
import VoiceCallTwilio from './VoiceCallTwilio';
import VoiceCallDaily from './VoiceCallDaily';
import CanvasView from './CanvasView';
import LocalParticipant from './LocalParticipant';
import RemoteParticipant from './RemoteParticipant';
import WaitingModalContent from './WaitingModalContent';
import IncreaseResolutionIcon from '../../icons/IncreaseResolutionIcon';
import DecreaseResolutionIcon from '../../icons/DecreaseResolutionIcon';
import toggleToast from '../../utils/messaging';

import { tDevice, IDevice } from '@homeporter/types';

import {
  IChat,
  ITranscriptPart,
  ISmsSendStatus,
} from '../../global/interfaces';

import { COLORS } from '../../utils/colors';
import { useBookingsContext } from '../../contexts/BookingsContext';
import { ca } from 'date-fns/locale';

const MemoizedDailyAudio = React.memo(DailyAudio);

interface ICurrentRemoteDevices {
  camera: string;
  microphone: string;
  speaker: string;
}

interface DeviceTrayProps {
  top?: string;
  name: tDevice;
  devices?: IDevice[];

  handleSelectDevice: (
    device: tDevice | undefined,
    deviceId: string | undefined,
  ) => void;
  currentRemoteDevices: ICurrentRemoteDevices;
}
const DeviceTray: React.FC<DeviceTrayProps> = ({
  top = '10.5rem',
  name,
  devices,
  handleSelectDevice,
  currentRemoteDevices,
}) => {
  const theme = useTheme();

  console.log('::::devices: ', devices);

  return (
    <Box
      position="absolute"
      top={top}
      left="4.4rem"
      zIndex={250}
      border={`.0625rem solid ${theme.palette.divider}`}>
      {devices?.map(({ device }: any) => {
        return (
          <Box
            key={device.deviceId}
            display="flex"
            alignItems="center"
            width="100%"
            onClick={() => handleSelectDevice(name, device.deviceId)}
            sx={{
              padding: '.75rem',
              cursor: 'pointer',
              color:
                device.deviceId === currentRemoteDevices[name]
                  ? COLORS.DARK_GREY
                  : COLORS.BLACK,
              bgcolor:
                device.deviceId === currentRemoteDevices[name]
                  ? COLORS.WHITE
                  : COLORS.LIGHT_GRAY,
              '&:hover': {
                bgcolor: COLORS.WHITE,
                color: COLORS.PRIMARY,
              },
            }}>
            <Typography padding="0 .5rem" ml=".4375rem" fontSize=".75rem">
              {device.label.replace(/\(([^)]*)\)?/, '$1').trim()}
              {device.deviceId === currentRemoteDevices[name]
                ? ' (Active)'
                : ''}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

interface IRoom {
  roomName: string;
  remoteVideoRef: React.RefObject<HTMLVideoElement>;
  handleHangUp: () => void;
}

const Room = ({ roomName, remoteVideoRef, handleHangUp }: IRoom) => {
  const styles = useInspectorStyles();
  const { getSessionLink, sendSessionLink, saveTranscribePart } = useSession();

  const { userSettings } = useBookingsContext();

  const {
    booking,
    remoteParticipant,
    selectedImage,
    setSelectedImage,
    setSelectedImageElement,
    setChatHistory,
    setHasUnreadChat,
    setHasUpdatedImages,
    zoom,
    setZoom,
    imagesMapRef,
    replaceImageUrl,
    setTranscriptParts,
    appendImage,
    canvasSketchSize,
    selectedImageElement,
    sipSessionId,
    transcribeState,
    setTranscribeState,
  } = useSessionContext();

  const [modalMessage, setModalMessage] = useState<string>('');

  const [remoteVideoSize, setRemoteVideoSize] = useState<any>();
  const [mobileResolution, setMobileResolution] = useState(1);

  const [hasMediaConsent, setHasMediaConsent] = useState(false);
  const [hasMediaConsentRequest, setHasMediaConsentRequest] =
    useState<string>('');

  const [smsSendStatus, setSmsSendStatus] = useState<ISmsSendStatus | null>(
    null,
  );
  const handleCloseWaitingModal = () => setSmsSendStatus(null);

  const [isMoreModalOpen, setIsMoreModalOpen] = useState(false);
  const [alternativeNumber, setAlternativeNumber] = useState('');

  const [copySuccess, setCopySuccess] = useState('');

  const [showZoomTray, setShowZoomTray] = useState(false);
  const [showResolutionTray, setShowResolutionTray] = useState(false);

  const [remoteDevices, setRemoteDevices] = useState<any>(null);
  const [currentRemoteDevices, setCurrentRemoteDevices] =
    useState<ICurrentRemoteDevices>({
      camera: '',
      microphone: '',
      speaker: '',
    });

  const [deviceVisible, setDeviceVisible] = useState<{
    [key in tDevice]: boolean;
  }>({
    camera: true,
    microphone: true,
    speaker: true,
  });

  const callObject = useDaily();
  const localParticipant = callObject?.participants().local;

  const [topology, setTopology] = useState<string>('...');
  useEffect(() => {
    setTopology(callObject?.meetingSessionState()?.topology || '');
  }, [callObject?.meetingSessionState()]);

  const changeTopology = () => {
    setTopology('...');

    const updateTopology = async () => {
      if (transcribeState !== 'off') {
        callObject?.stopTranscription();
      }

      await callObject?.setNetworkTopology(
        topology === 'sfu' ? { topology: 'peer' } : { topology: 'sfu' },
      );

      console.log(
        '::::callObject?.meetingSessionState()',
        callObject?.meetingSessionState(),
      );

      setTopology(callObject?.meetingSessionState()?.topology || '...');
    };

    try {
      updateTopology();
    } catch (e) {
      setTopology(callObject?.meetingSessionState()?.topology || '...');
    }
  };

  useEffect(() => {
    if (remoteParticipant) {
      handleCloseWaitingModal();
    } else {
      setHasMediaConsent(false);
      setHasMediaConsentRequest('');

      setDeviceVisible({
        camera: false,
        microphone: false,
        speaker: false,
      });
    }
  }, [remoteParticipant]);

  const handleOpenMoreModal = () => setIsMoreModalOpen(true);
  const handleCloseMoreModal = () => setIsMoreModalOpen(false);

  const handleNumberSubmit = async () => {
    console.log('Alternative number submitted:', alternativeNumber);
    if (!booking) return;

    handleCloseMoreModal();

    setSmsSendStatus({ status: 'sending', message: 'Sending invite...' });
    const response = await sendSessionLink({
      bookingSessionCode: booking.booking_session_code as string,
      type: 'invite',
      alternativeNumber,
      timestamp: new Date().getTime(),
    });

    setSmsSendStatus(response);
  };

  const handleCopyUrl = async () => {
    console.log('::::handleCopyUrl...');

    const sessionLink = await getSessionLink(
      booking?.booking_session_code as string,
    );

    if (sessionLink !== 'error') {
      navigator.clipboard.writeText(sessionLink).then(
        () => {
          setCopySuccess('URL copied!');
          setTimeout(() => setCopySuccess(''), 2000);
        },
        (err) => {
          console.error('Failed to copy: ', err);
        },
      );
    } else {
      setCopySuccess('Failed to copy!');
      setTimeout(() => setCopySuccess(''), 2000);
    }
  };

  const sendPayload = (data: any) => {
    if (!localParticipant) return;

    const payload = {
      senderId: localParticipant?.user_id,
      ...data,
    };

    if (payload.type !== 'mouse-move')
      console.log(`::::sendAppMessage type: ${payload.type}`, payload);
    sendAppMessage(payload, '*');
  };

  const sendAppMessage = useAppMessage({
    onAppMessage: ({ data, ...rest }: any) => {
      if (data?.type !== 'network-stats')
        console.log(
          `::::VirtualSession Room handleAppMessage: ${data?.type ? data.type : ''}`,
          data,
        );

      if (rest.fromId === 'transcription') {
        setTranscriptParts((prev: ITranscriptPart[]) => [...prev, { ...data }]);

        console.log('::::remoteParticipant: ', remoteParticipant);

        if (remoteParticipant && remoteParticipant['owner']) {
          if (transcribeState !== 'on') setTranscribeState('on');
          return;
        }

        if (
          !saveTranscribePart({
            bookingId: booking?.booking_id,
            part: data,
          })
        ) {
          toggleToast('Error saving transcipt part', 'error');
        }

        return;
      }

      switch (data?.type) {
        case 'video-settings':
          if (data?.devicePixelRatio) {
            setMobileResolution(Number(data.devicePixelRatio));
          }

          if (data?.viewWidth && data?.viewHeight) {
            setRemoteVideoSize({
              width: data?.viewWidth,
              height: data?.viewHeight,
            });
          }
          break;

        case 'modal-message':
          setModalMessage(data.message);
          console.log('::::modal-message: ', data.message);

          if (data.message.includes('Consent')) {
            setHasMediaConsent(false);
          }
          break;

        case 'set-camera-consent':
          setHasMediaConsentRequest('');
          setHasMediaConsent(data.value && !!data.devices);
          setRemoteDevices(data.devices);
          setDeviceVisible({
            camera: hasMediaConsentRequest === 'camera',
            microphone: hasMediaConsentRequest === 'microphone',
            speaker: hasMediaConsentRequest === 'speaker',
          });

          setCurrentRemoteDevices({
            camera: data.current.camera,
            microphone: data.current.microphone,
            speaker: data.current.speaker,
          });

          !data.value &&
            setModalMessage('Request to access camera has been declined.');
          break;

        case 'device-change-result':
          data.device &&
            setCurrentRemoteDevices({
              ...currentRemoteDevices,
              [data.device]: data.deviceId,
            });
          break;

        case 'append-image':
          setHasUpdatedImages(true);
          replaceImageUrl(data.image);
          console.log('data.image:', data.image); //need to replace //vault with url
          /*
          {
            "id": "1736368070198",
            "width": 390,
            "height": 844,
            "src": "/vault/042a80e6-a6cd-4bb5-a014-c01a25f4717d/ddbfa934-a8a9-4adf-bbd4-451d428832f4/1736368070198.webp",
            "thumb": {
                "width": 139,
                "height": 300,
                "src": "/vault/042a80e6-a6cd-4bb5-a014-c01a25f4717d/ddbfa934-a8a9-4adf-bbd4-451d428832f4/1736368070198-thumb.webp"
            },
            "layer": 0,
            "strokes": [
                []
            ],
            "date": "2025-01-08T20:27:50.216Z",
            "type": "image",
            "_processed": true,
            "ratio": 0.46208530805687204
        }

          `${IMAGES_BASE_URL}/` +
          item.src
            .replace('https://vhc-pilot-v2.s3.amazonaws.com/', '')
            .replace(IMAGES_BASE_URL, '')
            .replace('vault/', '');

        */
          if (!imagesMapRef.current) {
            alert('Unable to store image');
            return;
          }

          appendImage(data.image);
          /*
          imagesMapRef.current.set(data.image.id, data.image);
          setTranscriptParts((prev: ITranscriptPart[]) => [
            ...prev,
            data.image,
          ]);
          */
          break;

        case 'image-upload-error':
          alert('Unable to store image');
          break;

        case 'chat':
          setHasUnreadChat(true);
          setChatHistory((prev: IChat[]) => [...prev, data]);
          break;
      }
    },
  });

  const sendInvite = async () => {
    console.log('::::sendInvite...');
    if (!booking) return;

    setSmsSendStatus({
      status: 'sending',
      message: 'Sending invite...',
    });
    const response = await sendSessionLink({
      bookingSessionCode: booking.booking_session_code as string,
      type: 'invite',
      timestamp: new Date().getTime(),
    });

    console.log('::::handleNumberSubmit response:', response);
    setSmsSendStatus(response);
  };

  useEffect(() => {
    if (!remoteVideoRef?.current) return;

    console.log('::::selectedImage: ', selectedImage);

    selectedImage?.id !== '' &&
      sendPayload({
        type: 'selected-image',
        selectedImageId: selectedImage?.id || 'null',
      });

    if (selectedImage) remoteVideoRef.current.pause();
    else
      remoteVideoRef.current.play().catch((error: DOMException) => {
        console.error('Error attempting to play:', error);
      });
  }, [selectedImage]);

  const handleZoom = (direction: string) => {
    const _zoom = zoom + (direction === 'in' ? 0.25 : -0.25);

    setZoom(_zoom);

    if (!remoteVideoRef.current) return;
    remoteVideoRef.current.style.transform = `scale(${_zoom})`;
    sendPayload({
      type: 'zoom',
      zoom: _zoom,
    });
  };

  const handleTakeScreenCapture = () => {
    if (
      remoteVideoSize &&
      remoteVideoSize.height !== undefined &&
      remoteVideoSize.width !== undefined &&
      remoteVideoRef.current !== null
    ) {
      setSelectedImageElement(null);

      console.log(
        `::::handleTakeScreenCapture: ${remoteVideoSize.width} x ${remoteVideoSize.height}`,
      );

      setSelectedImage({
        type: 'image',
        height: remoteVideoSize.height,
        width: remoteVideoSize.width,
        id: '',
        layer: 0,
        src: '',
        thumb: { height: 0, width: 0, src: '' },
        strokes: [[]],
      });

      sendPayload({
        type: 'capture',
      });
    }
  };

  const handleSelectDevice = (
    device: tDevice | undefined = undefined,
    deviceId: string | undefined = undefined,
  ) => {
    if (device && deviceVisible[device] && !deviceId) {
      setDeviceVisible({ ...deviceVisible, [device]: false });
    } else if (device && !hasMediaConsent) {
      setHasMediaConsentRequest(device);
      sendPayload({
        type: 'media-consent-request',
        request: true,
      });
    } else if (!device) {
      setDeviceVisible({
        camera: false,
        microphone: false,
        speaker: false,
      });
    } else if (!deviceId) {
      setDeviceVisible({
        camera: false,
        microphone: false,
        speaker: false,
        [device]: true,
      });
    } else {
      setDeviceVisible({
        camera: false,
        microphone: false,
        speaker: false,
      });
      sendPayload({
        type: `set-${device}`,
        deviceId,
      });
    }
  };

  const handleMobileResolutionChange = (direction: string) => {
    if (direction === 'increase') {
      if (mobileResolution < 4) {
        sendPayload({
          type: 'set-device-pixel-ratio',
          devicePixelRatio: mobileResolution + 0.25,
        });
      }
    } else {
      if (mobileResolution > 1) {
        sendPayload({
          type: 'set-device-pixel-ratio',
          devicePixelRatio: mobileResolution - 0.25,
        });
      }
    }
  };

  const LEFT_SIDEBAR_ICONS = [
    {
      Icon: ZoomIcon,
      label: 'Zoom',
      disabled: false,
      mouseEnter: () => {
        setShowZoomTray(true);
      },
      mouseLeave: () => {
        setShowZoomTray(false);
      },
      showTray: showZoomTray,
      trayItems: [
        {
          Icon: ZoomInIcon,
          label: 'In',
          handleClick: () => handleZoom('in'),
          disabled: zoom >= 3,
        },
        {
          Icon: ZoomOutIcon,
          label: 'Out',
          handleClick: () => handleZoom('out'),
          disabled: zoom <= 1,
        },
      ],
      trayStyle: {
        position: 'absolute',
        left: '100%',
        top: 0,
        backgroundColor: 'rgba(0,0,0,0.8)',
        borderRadius: '4px',
        ml: '0.125rem',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 1,
      },
    },
    //    ...(userSettings?.dev
    //      ? [
    {
      Icon: ResolutionIcon,
      label: 'Resolution',
      disabled: false,
      mouseEnter: () => setShowResolutionTray(true),
      mouseLeave: () => setShowResolutionTray(false),
      showTray: showResolutionTray,
      trayItems: [
        {
          Icon: IncreaseResolutionIcon,
          label: 'Resolution',
          handleClick: () => handleMobileResolutionChange('increase'),
          disabled: mobileResolution > 4,
        },
        {
          Icon: DecreaseResolutionIcon,
          label: '',
          handleClick: () => handleMobileResolutionChange('decrease'),
          disabled: mobileResolution <= 1,
        },
      ],
      trayStyle: {
        position: 'absolute',
        left: '100%',
        top: '6rem',
        backgroundColor: 'rgba(0,0,0,0.8)',
        borderRadius: '4px',
        ml: '0.125rem',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 1,
      },
    },
    //        ]
    //      : []),
    {
      Icon: ChangeCameraIcon,
      label: 'Camera',
      handleClick: () => handleSelectDevice('camera'),
      //mouseEnter: () => hasMediaConsent && handleSelectDevice('camera'),
      //mouseLeave: () => handleSelectDevice(),
      disabled: false,
    },
    {
      Icon: MicrophoneIcon,
      label: 'Microphone',
      handleClick: () => handleSelectDevice('microphone'),
      //mouseEnter: () => hasMediaConsent && handleSelectDevice('microphone'),
      //mouseLeave: () => handleSelectDevice(),
      disabled: false,
    },
    {
      Icon: SpeakerIcon,
      label: 'Speaker',
      handleClick: () => handleSelectDevice('speaker'),
      //mouseEnter: () => hasMediaConsent && handleSelectDevice('speaker'),
      //mouseLeave: () => handleSelectDevice(),
      disabled: false,
    },
    {
      Icon: CaptureIcon,
      label: 'Capture',
      handleClick: handleTakeScreenCapture,
      disabled: false,
      sx: styles.captureButton,
    },
  ];

  useEffect(() => {
    console.log('::::construct Room');
    return () => {
      console.log('::::destroy Room');
    };
  }, []);

  console.log(':::Room...');
  return (
    <>
      <Box width="100%">
        <Modal
          open={!!modalMessage}
          onClose={handleCloseWaitingModal}
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{
            backdrop: {
              timeout: 300,
            },
          }}>
          <ModalContentWrapper>
            <IconButton
              sx={styles.closeConsentModal}
              onClick={() => setModalMessage('')}>
              <Close />
            </IconButton>
            <Typography sx={styles.consentModal}>{modalMessage}</Typography>
          </ModalContentWrapper>
        </Modal>

        <Modal
          open={isMoreModalOpen}
          onClose={handleCloseMoreModal}
          aria-labelledby="alternative-number-modal"
          aria-describedby="enter-alternative-number">
          <Box sx={styles.modalBox}>
            <Typography
              id="alternative-number-modal"
              variant="h6"
              component="h2">
              Enter Alternative Number
            </Typography>
            <Box sx={styles.alternativeNumberBox}>
              <TextField
                label="Alternative Number"
                variant="outlined"
                fullWidth
                value={alternativeNumber}
                onChange={(e) => setAlternativeNumber(e.target.value)}
                sx={styles.alternativeNumberInput}
              />
              <CustomButton
                handleClick={handleNumberSubmit}
                style={{
                  ...styles.sendSessionLink,
                  minWidth: '5rem',
                  marginLeft: '1rem',
                }}>
                Send
              </CustomButton>
            </Box>
            <Box sx={styles.alternativeNumberButtons}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  onClick={handleCopyUrl}
                  sx={{ mr: 2, padding: '0.25rem 1rem' }}>
                  Copy Session URL
                </Button>
                {copySuccess && (
                  <Typography variant="body2">{copySuccess}</Typography>
                )}
              </Box>
              <Button
                onClick={handleCloseMoreModal}
                sx={{ alignSelf: 'flex-start', padding: '0.25rem 1rem' }}>
                Close
              </Button>
            </Box>
          </Box>
        </Modal>

        <Fade in={!hasMediaConsent && !!hasMediaConsentRequest}>
          <Box sx={styles.sidebarConsent}>
            <Typography style={styles.flipCamera}>
              Waiting on HomeOwner consent to access media devices.
            </Typography>
          </Box>
        </Fade>

        <Box
          onClick={() => {
            handleSelectDevice();
          }}
          style={{ ...styles.room, position: 'absolute', top: 0 }}
          display={!selectedImageElement ? 'flex' : 'none'}>
          <Box sx={{ ...styles.sidebar, opacity: selectedImage ? 0 : 1 }}>
            {LEFT_SIDEBAR_ICONS.map(
              (
                {
                  Icon,
                  label,
                  mouseEnter,
                  mouseLeave,
                  handleClick,
                  disabled,
                  showTray,
                  trayItems,
                  trayStyle,
                  sx,
                },
                index,
              ) => (
                <Box
                  key={index}
                  sx={{ ...styles.sidebarLeftButton, ...sx }}
                  onMouseEnter={() => {
                    !!remoteParticipant && !disabled && mouseEnter?.();
                  }}
                  onMouseLeave={() => {
                    !!remoteParticipant && !disabled && mouseLeave?.();
                  }}>
                  <IconButton
                    disabled={!remoteParticipant || disabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClick?.();
                    }}
                    sx={styles.sidbarIcon}>
                    <Icon
                      strokeWidth={1.2}
                      sx={{
                        fontSize: '1.75rem',
                        color:
                          !remoteParticipant || disabled
                            ? '#555'
                            : COLORS.LIGHT_GRAY,
                        '&:hover': {
                          color: COLORS.PRIMARY,
                        },
                      }}
                    />
                  </IconButton>

                  <Typography
                    sx={{
                      ...styles.sidebarButtonLabel,
                      color: !remoteParticipant ? '#555' : '#fff',
                      whiteSpace: label.includes('\n') ? 'pre-line' : 'normal',
                    }}>
                    {label}
                  </Typography>

                  {!disabled && trayItems && (
                    <Fade in={showTray} timeout={200}>
                      <Box sx={trayStyle}>
                        {trayItems?.map((item, idx) => (
                          <IconButton
                            key={idx}
                            disabled={item.disabled || !remoteParticipant}
                            onClick={item.handleClick}
                            sx={styles.sidbarIcon}>
                            <item.Icon
                              strokeWidth={1.2}
                              sx={{
                                fontSize: '1.875rem',
                                color:
                                  !remoteParticipant || item.disabled
                                    ? '#555'
                                    : COLORS.LIGHT_GRAY,
                                '&:hover': {
                                  color: COLORS.PRIMARY,
                                },
                              }}
                            />
                          </IconButton>
                        ))}
                      </Box>
                    </Fade>
                  )}
                </Box>
              ),
            )}

            <Box
              sx={
                !remoteParticipant
                  ? { ...styles.sidebarHangUp, bgcolor: 'transparent' }
                  : styles.sidebarHangUp
              }>
              <IconButton
                disabled={!remoteParticipant}
                onClick={handleHangUp}
                sx={{
                  border: '0',
                  '&:hover': { bgcolor: 'red' },
                }}>
                <CallEnd
                  sx={{
                    color: !remoteParticipant ? '#555' : '#fff',
                  }}
                />
              </IconButton>
            </Box>
            {userSettings?.dev && (
              <>
                <Button
                  title="Change Network Topology"
                  sx={{ mt: 'auto', mb: '1rem' }}
                  onClick={changeTopology}>
                  {topology}
                </Button>
                <Button
                  sx={{ mb: '15rem' }}
                  onClick={() => {
                    if (!callObject) return;

                    callObject.setSubscribeToTracksAutomatically(false);
                    if (remoteParticipant?.session_id) {
                      callObject.updateParticipant(
                        remoteParticipant?.session_id,
                        {
                          setSubscribedTracks: { video: false },
                        },
                      );
                    }
                    console.log('stopped video...');
                    setTimeout(() => {
                      if (remoteParticipant?.session_id) {
                        callObject.updateParticipant(
                          remoteParticipant?.session_id,
                          {
                            setSubscribedTracks: { video: true },
                          },
                        );
                      }
                      callObject.setSubscribeToTracksAutomatically(true);

                      console.log('started video...\nrestart  remotevideo...');
                      sendPayload({
                        type: 'restart-video',
                      });
                    }, 500);
                  }}>
                  restart
                </Button>
              </>
            )}
          </Box>

          {(['camera', 'microphone', 'speaker'] as const).map((type, index) => {
            const devices =
              remoteDevices?.[`${type}s` as keyof typeof remoteDevices];
            return (
              deviceVisible[type] &&
              devices && (
                <DeviceTray
                  key={type}
                  top={`${11 + index * 4.5}rem`}
                  name={type}
                  devices={devices}
                  handleSelectDevice={handleSelectDevice}
                  currentRemoteDevices={currentRemoteDevices}
                />
              )
            );
          })}

          <Box
            id="_remote-participant"
            style={{
              backgroundColor: !!selectedImage ? '#fff' : COLORS.DARK_GREY,
            }}
            sx={styles.remotePanel as React.CSSProperties}>
            <RemoteParticipant
              remoteVideoRef={remoteVideoRef}
              sendPayload={sendPayload}
              remoteVideoSize={remoteVideoSize}
              participant={remoteParticipant}
            />

            <Box
              sx={{
                ...styles.startButtons,
                display: !remoteParticipant ? 'flex' : 'none',
              }}>
              {smsSendStatus !== null ? (
                <WaitingModalContent
                  smsSendStatus={smsSendStatus}
                  mobileNumber={
                    alternativeNumber || booking?.mobile_number || ''
                  }
                  handleCloseWaitingModal={handleCloseWaitingModal}
                  bookingId={booking?.booking_id?.toString()}
                />
              ) : (
                <>
                  <CustomButton
                    handleClick={sendInvite}
                    style={styles.sendSessionLink}>
                    <ChatBubbleOutline sx={{ marginRight: '0.5rem' }} />
                    Invite HomeOwner
                  </CustomButton>

                  {userSettings?.dev && (
                    <VoiceCallTwilio
                      styles={styles}
                      phoneNumber={
                        alternativeNumber || booking?.mobile_number || ''
                      }
                    />
                  )}

                  <VoiceCallDaily
                    styles={styles}
                    roomName={roomName}
                    sipSessionId={sipSessionId}
                    phoneNumber={
                      alternativeNumber || booking?.mobile_number || ''
                    }
                  />

                  <Button onClick={handleOpenMoreModal} sx={styles.moreOptions}>
                    More options
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>

        <CanvasView canvasSketchSize={canvasSketchSize} />
      </Box>

      <LocalParticipant participant={localParticipant} />
      <MemoizedDailyAudio />
    </>
  );
};

export default Room;
